// src/crm/ugc/ApplicationsTab.tsx
//
// Onglet « Candidatures » du programme UGC (spec § 6.1) : en attente d'abord, filtres « Acceptées »
// et « Refusées ». Accepter crée ou rattache le contact du CRM et envoie l'e-mail de bienvenue (avec,
// en option, le montant pour 1 000 vues de la personne, sinon à fixer depuis sa fiche) ;
// Refuser ouvre, dans la page, un message facultatif ajouté à l'e-mail de refus.

import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { highRateQuestion, parseRateInput } from '../../../functions/lib/ugc/memberRate'
import { FOLLOWERS_BANDS, type ApplicationStatus, type FollowersBand, type UgcPlatform } from '../../lib/ugcTypes'
import { formatDate } from './format'
import { HighRateConfirm } from './HighRateConfirm'
import {
  useAcceptApplication,
  useRejectApplication,
  useUgcApplications,
  useUgcMembers,
  type AdminApplication,
} from './hooks'

const FILTERS: readonly { status: ApplicationStatus; label: string; empty: string }[] = [
  { status: 'pending', label: 'En attente', empty: 'Aucune candidature en attente.' },
  { status: 'accepted', label: 'Acceptées', empty: 'Aucune candidature acceptée.' },
  { status: 'rejected', label: 'Refusées', empty: 'Aucune candidature refusée.' },
]

const PLATFORM_LABELS: Record<UgcPlatform, string> = { tiktok: 'TikTok', instagram: 'Instagram' }

function profileUrl(platform: UgcPlatform, handle: string): string {
  return platform === 'tiktok' ? `https://www.tiktok.com/@${handle}` : `https://www.instagram.com/${handle}/`
}

function bandLabel(band: FollowersBand | null): string | null {
  const found = FOLLOWERS_BANDS.find((b) => b.value === band)
  return found ? `${found.label} abonnés` : null
}

function referrerText(app: AdminApplication): string {
  if (app.referralCode === null) return 'Aucun'
  return `${app.referralCode} (${app.referrerName ?? 'aucun membre avec ce code'})`
}

/** Lien vers la fiche contact du membre (liste des membres partagée entre les cartes). */
function ContactLink({ memberId }: { memberId: string }) {
  const members = useUgcMembers()
  if (members.isError) return <span className="ugc-muted">Fiche contact indisponible</span>
  const creatorId = members.data?.find((m) => m.id === memberId)?.creatorId
  if (!creatorId) return null
  return (
    <Link className="ugc-link" to={`/crm/contacts/${creatorId}`}>
      Fiche contact
    </Link>
  )
}

function Accounts({ app }: { app: AdminApplication }) {
  const accounts = (
    [
      ['tiktok', app.tiktokHandle, app.tiktokFollowers],
      ['instagram', app.instagramHandle, app.instagramFollowers],
    ] as const
  ).filter((a): a is readonly [UgcPlatform, string, FollowersBand | null] => a[1] !== null)
  if (accounts.length === 0) return <>Aucun</>
  return (
    <ul className="ugc-accounts">
      {accounts.map(([platform, handle, band]) => (
        <li key={platform}>
          <a className="ugc-link" href={profileUrl(platform, handle)} target="_blank" rel="noreferrer">
            {PLATFORM_LABELS[platform]} @{handle}
          </a>
          {bandLabel(band) && <span className="ugc-muted">{bandLabel(band)}</span>}
        </li>
      ))}
    </ul>
  )
}

function PendingActions({ app, onAccepted }: { app: AdminApplication; onAccepted: (name: string, creatorId: string) => void }) {
  const accept = useAcceptApplication()
  const reject = useRejectApplication()
  // Accepter et refuser passent tous deux par une confirmation dans la page (envoi d'e-mail, sans retour).
  const [step, setStep] = useState<'idle' | 'accepting' | 'rejecting'>('idle')
  const [message, setMessage] = useState('')
  const [rate, setRate] = useState('')
  const messageId = useId()
  const rateId = useId()
  const busy = accept.isPending || reject.isPending
  const error = accept.error ?? reject.error
  // Montant pour 1 000 vues facultatif : vide = à fixer plus tard depuis la fiche.
  const rateCents = parseRateInput(rate)
  const rateInvalid = rate.trim() !== '' && rateCents === null
  // Montant élevé (plus de 10 € pour 1 000 vues) : confirmé avant l'acceptation.
  const [confirmingRate, setConfirmingRate] = useState(false)

  function handleAccept(confirmed = false) {
    reject.reset()
    if (!confirmed && rateCents !== null && highRateQuestion(rateCents) !== null) {
      setConfirmingRate(true)
      return
    }
    setConfirmingRate(false)
    accept.mutate(
      { id: app.id, rateCentsPerThousand: rateCents ?? undefined },
      { onSuccess: ({ member }) => onAccepted(app.displayName, member.creatorId) },
    )
  }

  function handleReject() {
    accept.reset()
    const trimmed = message.trim()
    reject.mutate({ id: app.id, message: trimmed === '' ? null : trimmed })
  }

  return (
    <>
      {step === 'accepting' && (
        <div className="ugc-inline-form">
          <p className="ugc-confirm">
            Le contact sera créé dans le CRM et l'e-mail de bienvenue envoyé à {app.email}.
          </p>
          <div className="ugc-field">
            <label htmlFor={rateId}>Montant pour 1 000 vues, en euros (facultatif)</label>
            <div className="ugc-rate-input">
              <input
                id={rateId}
                className="ugc-input"
                inputMode="decimal"
                autoComplete="off"
                placeholder="2,50"
                value={rate}
                aria-invalid={rateInvalid}
                aria-describedby={`${rateId}-help`}
                onChange={(e) => {
                  setRate(e.target.value)
                  setConfirmingRate(false)
                }}
              />
              <span aria-hidden="true">€</span>
            </div>
          </div>
          <p id={`${rateId}-help`} className="ugc-muted">
            {rateInvalid
              ? 'Écris un montant en euros, de 0,01 à 100 (par exemple 2,50), ou laisse vide.'
              : "Vide : à fixer plus tard depuis sa fiche ; la personne voit « En cours d'analyse du montant attribuable »."}
          </p>
          {confirmingRate && rateCents !== null && (
            <HighRateConfirm
              question={highRateQuestion(rateCents) ?? ''}
              busy={busy}
              onConfirm={() => handleAccept(true)}
              onCancel={() => setConfirmingRate(false)}
            />
          )}
          <div className="ugc-actions">
            <button type="button" className="ugc-btn ugc-btn--go" onClick={() => handleAccept()} disabled={busy || rateInvalid}>
              {accept.isPending ? 'Acceptation…' : "Confirmer l'acceptation"}
            </button>
            <button type="button" className="ugc-btn" onClick={() => setStep('idle')} disabled={busy}>
              Annuler
            </button>
          </div>
        </div>
      )}
      {step === 'rejecting' && (
        <div className="ugc-inline-form">
          <label htmlFor={messageId}>Message ajouté à l'e-mail de refus (facultatif)</label>
          <textarea
            id={messageId}
            className="ugc-textarea"
            rows={3}
            maxLength={1000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="ugc-actions">
            <button type="button" className="ugc-btn ugc-btn--strong" onClick={handleReject} disabled={busy}>
              Confirmer le refus
            </button>
            <button type="button" className="ugc-btn" onClick={() => setStep('idle')} disabled={busy}>
              Annuler
            </button>
          </div>
        </div>
      )}
      {step === 'idle' && (
        <div className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--go" onClick={() => setStep('accepting')}>
            Accepter
          </button>
          <button type="button" className="ugc-btn" onClick={() => setStep('rejecting')}>
            Refuser
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="ugc-error">
          {error.message}
        </p>
      )}
    </>
  )
}

function ApplicationCard({
  app,
  onAccepted,
}: {
  app: AdminApplication
  onAccepted: (name: string, creatorId: string) => void
}) {
  const titleId = useId()
  return (
    <article className="ugc-card" aria-labelledby={titleId}>
      <header className="ugc-card-head">
        <h2 id={titleId} className="ugc-card-title">
          {app.displayName}
        </h2>
        <span className="ugc-muted">Reçue le {formatDate(app.createdAt)}</span>
      </header>
      <dl className="ugc-facts">
        <div>
          <dt>E-mail</dt>
          <dd>
            <a className="ugc-link" href={`mailto:${app.email}`}>
              {app.email}
            </a>
          </dd>
        </div>
        <div>
          <dt>Comptes</dt>
          <dd>
            <Accounts app={app} />
          </dd>
        </div>
        <div>
          <dt>Public en France</dt>
          <dd>{app.audienceFrance ? 'Oui' : 'Non'}</dd>
        </div>
        <div>
          <dt>Peut facturer</dt>
          <dd>{app.canInvoice ? 'Oui' : 'Non'}</dd>
        </div>
        <div>
          <dt>Parrain</dt>
          <dd>{referrerText(app)}</dd>
        </div>
      </dl>
      {app.status === 'pending' && <PendingActions app={app} onAccepted={onAccepted} />}
      {app.status === 'accepted' && (
        <div className="ugc-card-foot">
          {app.decidedAt && <span className="ugc-muted">Acceptée le {formatDate(app.decidedAt)}</span>}
          {app.memberId && <ContactLink memberId={app.memberId} />}
        </div>
      )}
      {app.status === 'rejected' && (
        <div className="ugc-card-foot">
          {app.decidedAt && <span className="ugc-muted">Refusée le {formatDate(app.decidedAt)}</span>}
          {app.decisionMessage && <p className="ugc-quote">{`« ${app.decisionMessage} »`}</p>}
        </div>
      )}
    </article>
  )
}

export function ApplicationsTab() {
  const [status, setStatus] = useState<ApplicationStatus>('pending')
  const [accepted, setAccepted] = useState<{ name: string; creatorId: string } | null>(null)
  const applications = useUgcApplications(status)
  const filter = FILTERS.find((f) => f.status === status) ?? FILTERS[0]!

  return (
    <section className="ugc-tab-panel" aria-label="Candidatures">
      <div className="ugc-filters" role="group" aria-label="Filtrer les candidatures">
        {FILTERS.map((f) => (
          <button
            key={f.status}
            type="button"
            className="ugc-filter"
            aria-pressed={f.status === status}
            onClick={() => setStatus(f.status)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {accepted && (
        <p role="status" className="ugc-notice">
          Candidature de {accepted.name} acceptée.{' '}
          <Link className="ugc-link" to={`/crm/contacts/${accepted.creatorId}`}>
            Ouvrir la fiche contact
          </Link>
        </p>
      )}

      {applications.isPending && <p className="ugc-muted">Chargement…</p>}
      {applications.isError && <p className="ugc-error">Les candidatures n'ont pas pu être chargées : {applications.error.message}</p>}
      {applications.data && applications.data.length === 0 && <p className="ugc-empty">{filter.empty}</p>}
      {applications.data && applications.data.length > 0 && (
        <div className="ugc-cards">
          {applications.data.map((app) => (
            <ApplicationCard key={app.id} app={app} onAccepted={(name, creatorId) => setAccepted({ name, creatorId })} />
          ))}
        </div>
      )}
    </section>
  )
}
